import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  App, Badge, Button, Drawer, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip,
} from 'antd';
import {
  DeleteOutlined, EditOutlined, KeyOutlined, LinkOutlined, PlusOutlined, ReloadOutlined,
  SearchOutlined, SafetyOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  AdminRoleVo, AdminUserVo, assignUserRoles, bindUserLocal, createUser, deleteUser,
  listRoles, listUsers, resetUserPassword, unbindUserFeishu, updateUser,
} from '@apis/admin';

const formatTime = (v?: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-');

interface CreateForm {
  email: string;
  name: string;
  username: string;
  password: string;
  roleCodes: string[];
}

interface RoleForm { roleCodes: string[] }
interface ResetForm { newPassword: string }
interface BindLocalForm { username: string; password: string }

export default function UserTab() {
  const { message, modal } = App.useApp();

  const [data, setData] = useState<AdminUserVo[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<AdminRoleVo[]>([]);

  // 弹窗状态
  const [creating, setCreating] = useState(false);
  const [drawerUser, setDrawerUser] = useState<AdminUserVo | null>(null);
  const [createForm] = Form.useForm<CreateForm>();
  const [roleForm] = Form.useForm<RoleForm>();
  const [resetForm] = Form.useForm<ResetForm>();
  const [bindForm] = Form.useForm<BindLocalForm>();

  const loadRoles = useCallback(async () => {
    try {
      setRoles(await listRoles());
    } catch (e: any) {
      message.error(e?.message || '加载角色失败');
    }
  }, [message]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { items, page: meta } = await listUsers({
        keyword: keyword || undefined,
        role: roleFilter,
        status: statusFilter,
        current: page,
        pageSize,
      });
      setData(items);
      setTotal(meta?.totalElements ?? 0);
    } catch (e: any) {
      message.error(e?.message || '加载用户失败');
    } finally {
      setLoading(false);
    }
  }, [keyword, roleFilter, statusFilter, page, pageSize, message]);

  useEffect(() => { loadRoles(); }, [loadRoles]);
  useEffect(() => { loadList(); }, [loadList]);

  const roleOptions = useMemo(
    () => roles.map((r) => ({ label: `${r.name} (${r.code})`, value: r.code })),
    [roles],
  );

  // ---- 创建用户 ----
  const submitCreate = async () => {
    const values = await createForm.validateFields();
    const result = await createUser(values);
    if (result.ok) {
      message.success('创建成功');
      setCreating(false);
      createForm.resetFields();
      loadList();
      return;
    }
    if (result.code === 409 && result.existing) {
      // 引导补绑流程
      const u = result.existing;
      modal.confirm({
        title: '该邮箱已存在用户',
        content: (
          <div>
            <p>该邮箱已绑定用户「{u.name}」（id={u.id}），登录方式：
              {u.hasLocal ? <Tag color="blue">本地</Tag> : null}
              {u.hasFeishu ? <Tag color="purple">飞书</Tag> : null}
            </p>
            <p>是否在该用户上「补绑本地账号」？（用刚才填写的用户名/密码）</p>
          </div>
        ),
        okText: '补绑本地',
        cancelText: '取消',
        okButtonProps: { disabled: u.hasLocal },
        onOk: async () => {
          try {
            await bindUserLocal(u.id, values.username, values.password);
            message.success('已为现存用户补绑本地账号');
            setCreating(false);
            createForm.resetFields();
            loadList();
          } catch (e: any) {
            message.error(e?.message || '补绑失败');
          }
        },
      });
    } else {
      message.error(result.message);
    }
  };

  // ---- 操作 ----
  const toggleStatus = async (u: AdminUserVo) => {
    const next = u.status === 1 ? 0 : 1;
    await updateUser(u.id, { status: next });
    message.success(next === 1 ? '已启用' : '已禁用');
    loadList();
  };

  const submitRoles = async () => {
    if (!drawerUser) return;
    const values = await roleForm.validateFields();
    await assignUserRoles(drawerUser.id, values.roleCodes);
    message.success('授权成功（旧 access_token 将在 60s 内失效）');
    setDrawerUser(null);
    loadList();
  };

  const submitReset = async (u: AdminUserVo) => {
    const values = await resetForm.validateFields();
    await resetUserPassword(u.id, values.newPassword);
    resetForm.resetFields();
    message.success('已重置（用户老 access_token 立即失效）');
  };

  const submitBindLocal = async (u: AdminUserVo) => {
    const values = await bindForm.validateFields();
    await bindUserLocal(u.id, values.username, values.password);
    bindForm.resetFields();
    message.success('已补绑本地登录');
    loadList();
  };

  const submitUnbindFeishu = async (u: AdminUserVo) => {
    await unbindUserFeishu(u.id);
    message.success('已解绑飞书');
    loadList();
  };

  const submitDelete = async (u: AdminUserVo) => {
    await deleteUser(u.id);
    message.success('已删除');
    loadList();
  };

  const columns: ColumnsType<AdminUserVo> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '用户',
      key: 'user',
      render: (_, u) => (
        <div>
          <div style={{ fontWeight: 500 }}>{u.name}</div>
          <div style={{ color: '#888', fontSize: 12 }}>{u.email}</div>
          {u.username && (
            <div style={{ color: '#888', fontSize: 12 }}>username: {u.username}</div>
          )}
        </div>
      ),
    },
    {
      title: '绑定',
      key: 'bindings',
      width: 160,
      render: (_, u) => (
        <Space size={4} wrap>
          <Tooltip title={u.hasLocal ? `绑定于 ${formatTime(u.boundAtLocal)}` : '未绑定'}>
            <Tag color={u.hasLocal ? 'blue' : 'default'}>本地</Tag>
          </Tooltip>
          <Tooltip title={u.hasFeishu ? `绑定于 ${formatTime(u.boundAtFeishu)}` : '未绑定'}>
            <Tag color={u.hasFeishu ? 'purple' : 'default'}>飞书</Tag>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '角色',
      dataIndex: 'roles',
      width: 220,
      render: (codes: string[]) =>
        codes.length === 0 ? <Tag>无</Tag> : codes.map((c) => <Tag key={c} color="green">{c}</Tag>),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (s) => <Badge status={s === 1 ? 'success' : 'default'} text={s === 1 ? '启用' : '禁用'} />,
    },
    { title: '最近登录', dataIndex: 'lastLoginAt', width: 150, render: formatTime },
    {
      title: '操作',
      key: 'op',
      width: 360,
      render: (_, u) => (
        <Space wrap size={4}>
          <Button
            size="small"
            icon={<SafetyOutlined />}
            onClick={() => {
              roleForm.setFieldsValue({ roleCodes: u.roles });
              setDrawerUser(u);
            }}
          >
            授角色
          </Button>
          <Popconfirm
            title="重置密码"
            description={
              <Form form={resetForm} layout="vertical" style={{ width: 220 }}>
                <Form.Item
                  label="新密码"
                  name="newPassword"
                  rules={[{ required: true, min: 8, message: '至少 8 位' }]}
                >
                  <Input.Password />
                </Form.Item>
              </Form>
            }
            onConfirm={() => submitReset(u)}
            okText="确认"
          >
            <Button size="small" icon={<KeyOutlined />} disabled={!u.hasLocal}>重置密码</Button>
          </Popconfirm>
          {!u.hasLocal && (
            <Popconfirm
              title="补绑本地账号"
              description={
                <Form form={bindForm} layout="vertical" style={{ width: 240 }}>
                  <Form.Item label="用户名" name="username" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    label="密码"
                    name="password"
                    rules={[{ required: true, min: 8 }]}
                  >
                    <Input.Password />
                  </Form.Item>
                </Form>
              }
              onConfirm={() => submitBindLocal(u)}
              okText="补绑"
            >
              <Button size="small" icon={<LinkOutlined />}>补绑本地</Button>
            </Popconfirm>
          )}
          {u.hasFeishu && (
            <Popconfirm
              title="解绑飞书后将无法用飞书登录该账号"
              onConfirm={() => submitUnbindFeishu(u)}
              okText="解绑"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" disabled={!u.hasLocal}>解绑飞书</Button>
            </Popconfirm>
          )}
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => toggleStatus(u)}
          >
            {u.status === 1 ? '禁用' : '启用'}
          </Button>
          <Popconfirm title={`确认删除用户 ${u.name}?`} onConfirm={() => submitDelete(u)} okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          placeholder="搜索 email / username / name"
          prefix={<SearchOutlined />}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onPressEnter={() => { setPage(1); loadList(); }}
          allowClear
          style={{ width: 240 }}
        />
        <Select
          placeholder="按角色"
          style={{ width: 180 }}
          allowClear
          options={roleOptions}
          value={roleFilter}
          onChange={(v) => { setRoleFilter(v); setPage(1); }}
        />
        <Select
          placeholder="状态"
          style={{ width: 120 }}
          allowClear
          options={[{ label: '启用', value: 1 }, { label: '禁用', value: 0 }]}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => loadList()}>刷新</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>新增用户</Button>
      </Space>

      <Table<AdminUserVo>
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100', '1000'],
          onChange: (p, s) => { setPage(p); setPageSize(s); },
        }}
      />

      {/* 新增用户 Modal */}
      <Modal
        title="新增本地账号"
        open={creating}
        onCancel={() => { setCreating(false); createForm.resetFields(); }}
        onOk={submitCreate}
        okText="创建"
      >
        <Form form={createForm} layout="vertical" initialValues={{ roleCodes: ['viewer'] }}>
          <Form.Item label="邮箱（唯一身份锚）" name="email" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="xxx@neolix.ai" />
          </Form.Item>
          <Form.Item label="姓名" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="用户名" name="username" rules={[{ required: true }]}>
            <Input placeholder="登录用，全平台唯一" />
          </Form.Item>
          <Form.Item label="初始密码" name="password" rules={[{ required: true, min: 8, message: '至少 8 位' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item label="初始角色" name="roleCodes">
            <Select mode="multiple" options={roleOptions} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 授角色 Drawer */}
      <Drawer
        title={drawerUser ? `给 ${drawerUser.name} 授角色` : ''}
        open={!!drawerUser}
        onClose={() => setDrawerUser(null)}
        width={420}
        extra={<Button type="primary" onClick={submitRoles}>保存</Button>}
      >
        <Form form={roleForm} layout="vertical">
          <Form.Item label="角色（多选）" name="roleCodes">
            <Select mode="multiple" options={roleOptions} placeholder="留空则该用户无任何角色" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
